import React, { useCallback, useContext } from 'react';
import type { DocumentType } from '../../graphql/generated';
import { gql } from '../../graphql/generated';
import type { DownloadAttachmentsProvider_PostFragment } from '../../graphql/generated/graphql';
import download from '../../utils/network/download';
import type { AttachmentFile } from '../formats/Attachment';
import showEditorToast from '../utils/showEditorToast';

type DownloadAttachmentsFn = (attachments: AttachmentFile[]) => Promise<void>;

const DownloadAttachmentsContext =
  React.createContext<DownloadAttachmentsFn | null>(null);

export const useDownloadAttachments = () =>
  useContext(DownloadAttachmentsContext);

const POST_FRAGMENT = gql(`
  fragment DownloadAttachmentsProvider_post on PostFields {
    id
    title
  }
`);

const downloadAll = ({
  attachments,
  post,
}: {
  attachments: AttachmentFile[];
  post: DocumentType<typeof POST_FRAGMENT>;
}) => {
  const query = new URLSearchParams(
    attachments.reduce((memo, { name, source }) => {
      const id = source.split('/').pop()?.replace(/\..+$/, '');
      const prefix = isLegacyGlobalAttachment(source, post.id)
        ? 'att_'
        : 'asset_';
      return id ? { ...memo, [`${prefix}${id}`]: name } : memo;
    }, {}),
  ).toString();

  const fileName = `Slab Attachments - ${post.title}.zip`;
  return download(`/posts/${post.id}/download?${query}`, fileName);
};

const isLegacyGlobalAttachment = (source: string, postId: string) =>
  !source.includes(`/post/${postId}/`);

interface DownloadAttachmentsProviderProps {
  children: React.ReactNode;
  post: DownloadAttachmentsProvider_PostFragment;
}

const DownloadAttachmentsProvider = ({
  children,
  post,
}: DownloadAttachmentsProviderProps) => {
  const downloadAttachments = useCallback<DownloadAttachmentsFn>(
    (attachments) => {
      return downloadAll({
        post,
        attachments,
      }).catch(() => {
        showEditorToast({
          level: 'error',
          message: 'Error while downloading attachments',
        });
      });
    },
    [post],
  );

  return (
    <DownloadAttachmentsContext.Provider value={downloadAttachments}>
      {children}
    </DownloadAttachmentsContext.Provider>
  );
};

export default DownloadAttachmentsProvider;
